import { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router";
import request from "superagent";
import Dialog from "@avinet/adaptive-ui-core/ui/Dialog";
import {
  TranslatorProvider,
  useTranslate,
} from "@avinet/adaptive-ui-translate";
import FormRenderer from "../components/form/FormRenderer";
import { Icon } from "../components/Icon";
import { LoadingOverlay } from "../components/Loading";
import { BACKEND_URL } from "../config/config";
import useApiSurveySpecification from "../hooks/useApiSurveySpecification";
import { useBrowserLanguage } from "../hooks/useBrowserLanguage";
import useTranslations from "../hooks/useTranslations";
import FormLayout from "../layouts/FormLayout";
import Survey from "../types/Survey";
import { normalizeData } from "../utils/api";

interface CollectionViewRendererProps {
  loading: boolean;
  survey?: Survey;
  error?: Error;
}

const CollectionViewRenderer = ({
  loading,
  survey,
  error,
}: CollectionViewRendererProps) => {
  const history = useHistory();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const goBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
    } else {
      window.close();
    }
  }, [history]);

  const onSubmit = useCallback(
    (data: Survey) => {
      if (!survey) return;
      setSaving(true);

      request
        .post(BACKEND_URL + `api/features/${survey.uuid}`)
        // TODO .set("Authorization", `Bearer ${sessionToken}`)
        .send({ data: normalizeData(survey, data), srid: 4326 })
        .then(() => {
          if (survey.metaCompletionRedirectUrl) {
            window.location.replace(survey.metaCompletionRedirectUrl);
          } else {
            history.replace(`/survey/${survey.uuid}/complete`);
          }
        })
        .catch((err) => {
          setSaving(false);
          console.error(err);
          setSaveError(true);
        });
    },
    [survey, history]
  );

  const t = useTranslate("CollectionView");
  return loading ? (
    <LoadingOverlay>{t("loading")}</LoadingOverlay>
  ) : survey ? (
    <FormLayout className={survey.layout}>
      <FormRenderer survey={survey} onSubmit={onSubmit} />
      {saving && <LoadingOverlay>{t("saving")}</LoadingOverlay>}
      {saveError && (
        <Dialog title={t("saveErrorTitle")} onClose={() => setSaveError(false)}>
          <p className="pre-wrap">{t("saveErrorText")}</p>
        </Dialog>
      )}
    </FormLayout>
  ) : (
    <div className="collection-view--load-error">
      <p className="big-icon">
        <Icon name="subtractCircle" />
      </p>
      <h2>{t("surveyNotActive")}</h2>
      {error?.message === "Not Found" ? (
        <p className="error">{t("pleasePublishFirst")}</p>
      ) : (
        <p className="error">{t("loadError")}</p>
      )}
      <button type="button" className="btn-link" onClick={goBack}>
        <Icon name="arrow" rotate={180} /> {t("goBack")}
      </button>
    </div>
  );
};

const CollectionView = () => {
  const { uuid, slug } = useParams<{ uuid?: string; slug?: string }>();

  const { survey, loading, error } = useApiSurveySpecification(uuid, slug);

  const browserLanguage = useBrowserLanguage();
  const translationLanguage = survey?.metaLanguage || browserLanguage;
  const lang = useTranslations(translationLanguage);

  return (
    <TranslatorProvider translations={lang}>
      <CollectionViewRenderer error={error} loading={loading} survey={survey} />
    </TranslatorProvider>
  );
};

export default CollectionView;
